import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  FolderOpen, Plus, FileText, Trash, PencilSimple, MagnifyingGlass,
  Tag, UploadSimple
} from "@phosphor-icons/react";

import Pagination from "@/components/Pagination";

const CATEGORIES = [
  { value: "vacancy_lists", label: "Vacancy Lists" },
  { value: "candidate_lists", label: "Candidate Lists" },
  { value: "employer_lists", label: "Target Employer Lists" },
  { value: "call_lists", label: "Call Lists" },
  { value: "scripts", label: "Scripts" },
  { value: "research", label: "Research" },
  { value: "training", label: "Training Material" },
  { value: "documents", label: "Documents" },
  { value: "general", label: "General" },
];

export default function TeamResourcesPage() {
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filter, setFilter] = useState({ team_id: "", category: "", search: "" });
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", category: "general", team_id: "", content: "", attachments: [] });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const isLeader = user?.role === "admin" || user?.role === "team_leader";

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.team_id) params.append("team_id", filter.team_id);
      if (filter.category) params.append("category", filter.category);
      params.set("page", page);
      params.set("limit", 25);
      const [rRes, tRes] = await Promise.all([
        api.get(`/team-resources?${params}`),
        api.get("/teams/dropdown"),
      ]);
      setResources(rRes.data.items || []);
      setTotal(rRes.data.total || 0);
      setTeams(tRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter.team_id, filter.category, page]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Resource name required"); return; }
    try {
      if (editing) {
        await api.put(`/team-resources/${editing}`, form);
        toast.success("Resource updated");
      } else {
        await api.post("/team-resources", form);
        toast.success("Resource created");
      }
      setShowCreate(false);
      setEditing(null);
      setForm({ name: "", description: "", category: "general", team_id: "", content: "", attachments: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try { await api.delete(`/team-resources/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const startEdit = (r) => {
    setForm({ name: r.name, description: r.description, category: r.category, team_id: r.team_id, content: r.content || "", attachments: r.attachments || [] });
    setEditing(r.id);
    setShowCreate(true);
  };

  const filtered = resources.filter(r =>
    !filter.search || r.name.toLowerCase().includes(filter.search.toLowerCase()) ||
    r.description?.toLowerCase().includes(filter.search.toLowerCase())
  );

  const catLabel = (v) => CATEGORIES.find(c => c.value === v)?.label || v;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="team-resources-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A]">Team Resources</h1>
          <p className="text-sm text-[#6B7280]">Reusable documents, lists, and materials for your team</p>
        </div>
        {isLeader && (
          <Button onClick={() => { setShowCreate(!showCreate); setEditing(null); setForm({ name: "", description: "", category: "general", team_id: "", content: "" }); }} className="bg-[#0F172A] gap-1.5" data-testid="create-resource-btn">
            <Plus size={14} /> Add Resource
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input placeholder="Search resources..." value={filter.search} onChange={e => setFilter({ ...filter, search: e.target.value })} className="pl-8 text-sm" />
        </div>
        <select className="border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm" value={filter.category} onChange={e => setFilter({ ...filter, category: e.target.value })} data-testid="filter-category">
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select className="border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm" value={filter.team_id} onChange={e => setFilter({ ...filter, team_id: e.target.value })} data-testid="filter-team">
          <option value="">All Teams</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      {/* Create/Edit Form */}
      {showCreate && isLeader && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 space-y-4" data-testid="resource-form">
          <Input placeholder="Resource name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="resource-name" />
          <Input placeholder="Short description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="resource-desc" />
          <div className="grid grid-cols-2 gap-3">
            <select className="border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="resource-category">
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <select className="border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm" value={form.team_id} onChange={e => setForm({ ...form, team_id: e.target.value })} data-testid="resource-team">
              <option value="">Select team</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <textarea
            className="w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm min-h-[120px] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
            placeholder="Resource content (lists, notes, instructions...)"
            value={form.content}
            onChange={e => setForm({ ...form, content: e.target.value })}
            data-testid="resource-content"
          />
          {/* File upload */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-[#374151]">Attachments (PDF, DOC, DOCX — max 10MB)</label>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                multiple
                className="text-xs"
                data-testid="resource-file-input"
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length === 0) return;
                  setUploading(true);
                  const newAttachments = [...(form.attachments || [])];
                  for (const file of files) {
                    if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} exceeds 10MB limit`); continue; }
                    const fd = new FormData();
                    fd.append("file", file);
                    try {
                      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      newAttachments.push({ name: file.name, url: data.url || data.file_url, size: file.size, type: file.type });
                      toast.success(`${file.name} uploaded`);
                    } catch (err) { toast.error(`Failed to upload ${file.name}`); }
                  }
                  setForm({ ...form, attachments: newAttachments });
                  setUploading(false);
                  e.target.value = "";
                }}
              />
              {uploading && <span className="text-xs text-[#C5A059]">Uploading...</span>}
            </div>
            {(form.attachments || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {form.attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-2 py-1 bg-[#F1F5F9] rounded text-xs">
                    <FileText size={12} className="text-[#C5A059]" />
                    <a href={a.url} target="_blank" rel="noreferrer" className="text-[#1E3A8A] hover:underline">{a.name}</a>
                    <button onClick={() => setForm({ ...form, attachments: form.attachments.filter((_, j) => j !== i) })} className="text-[#9CA3AF] hover:text-red-500 ml-1">&times;</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} className="bg-[#C5A059] text-[#0F172A] gap-1" data-testid="save-resource-btn">
              <UploadSimple size={14} /> {editing ? "Update" : "Save"} Resource
            </Button>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditing(null); }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Resources Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <div className="col-span-full bg-white rounded-xl border border-[#E5E7EB] p-8 text-center text-[#9CA3AF] text-sm">No resources found</div>
        ) : filtered.map(r => (
          <div key={r.id} className="bg-white rounded-xl border border-[#E5E7EB] p-5 hover:border-[#C5A059] transition" data-testid={`resource-${r.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-[#C5A059]" weight="duotone" />
                <h3 className="text-sm font-bold text-[#0F172A]">{r.name}</h3>
              </div>
              {isLeader && (
                <div className="flex gap-1">
                  <button onClick={() => startEdit(r)} className="text-[#6B7280] hover:text-[#0F172A] p-1"><PencilSimple size={14} /></button>
                  <button onClick={() => handleDelete(r.id)} className="text-[#6B7280] hover:text-red-600 p-1"><Trash size={14} /></button>
                </div>
              )}
            </div>
            {r.description && <p className="text-xs text-[#6B7280] mb-2">{r.description}</p>}
            <div className="flex items-center gap-2 text-[10px]">
              <span className="px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#374151] font-medium"><Tag size={10} className="inline mr-0.5" />{catLabel(r.category)}</span>
              <span className="text-[#9CA3AF]">{new Date(r.updated_at).toLocaleDateString("en-GB")}</span>
            </div>
            {r.content && (
              <div className="mt-3 p-2 bg-[#F9FAFB] rounded text-xs text-[#374151] max-h-24 overflow-y-auto whitespace-pre-wrap">{r.content.slice(0, 300)}{r.content.length > 300 ? "..." : ""}</div>
            )}
            {r.attachments?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {r.attachments.map((a, i) => (
                  <a key={i} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 rounded text-[10px] text-blue-700 hover:bg-blue-100">
                    <FileText size={10} /> {a.name}
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <Pagination page={page} total={total} limit={25} onPageChange={(p) => { setPage(p); setLoading(true); }} />
    </div>
  );
}
